import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PageCanvas from './components/PageCanvas';
import HotspotToolbar from './components/HotspotToolbar';
import HotspotConfigPanel from './components/HotspotConfigPanel';
import PageThumbnails from './components/PageThumbnails';
import HotspotList from './components/HotspotList';
import PreviewModal from './components/PreviewModal';
import CopyHotspotModal from './components/CopyHotspotModal';
import MenuMappingPanel from './components/MenuMappingPanel';
import PageKeywordsPanel from './components/PageKeywordsPanel';

import { catalogService } from '../../services/catalogService';
import { hotspotService } from '../../services/hotspotService';
import { useAuth } from '../../contexts/AuthContext';
import * as pdfjsLib from 'pdfjs-dist';
import EditorSkeleton from './components/EditorSkeleton';
import ErrorState from '../../components/ui/ErrorState';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Input from '../../components/ui/Input';
import Toast from '../../components/ui/Toast';
import ConfirmationModal from '../../components/ui/ConfirmationModal';

// Use CDN worker to avoid .mjs MIME type issues on servers without explicit mjs config
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const CatalogEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [selectedHotspotType, setSelectedHotspotType] = useState('link');
  const [selectedHotspotId, setSelectedHotspotId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showHotspotPanel, setShowHotspotPanel] = useState(false);
  const [showMenuPanel, setShowMenuPanel] = useState(false);
  const [rightTab, setRightTab] = useState('hotspots'); // 'hotspots' | 'menu' | 'page'
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved');
  const [catalogData, setCatalogData] = useState(null);
  const [pages, setPages] = useState([]);
  const [hotspots, setHotspots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPDF, setProcessingPDF] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [creatingHotspot, setCreatingHotspot] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [hotspotToCopy, setHotspotToCopy] = useState(null);
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [unpublishError, setUnpublishError] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const abortControllerRef = useRef(null);
  const publishTimeoutRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef?.current) {
        abortControllerRef?.current?.abort();
        abortControllerRef.current = null;
      }
      if (publishTimeoutRef?.current) {
        clearTimeout(publishTimeoutRef?.current);
        publishTimeoutRef.current = null;
      }
    };
  }, []);

  const removeToast = (id) => {
    setToasts(prev => prev?.filter(toast => toast?.id !== id));
  };

  useEffect(() => {
    if (!authLoading && user) {
      // Check if catalog ID was provided via URL search params (survives reload) or state (legacy)
      const catalogId = searchParams?.get('id') || location?.state?.catalogId;
      const selectedPDF = location?.state?.selectedPDF;
      
      if (catalogId) {
        loadCatalogById(catalogId);
      } else if (selectedPDF) {
        loadPDFPages(selectedPDF);
      } else {
        loadCatalogData();
      }
    }
    return () => {
      if (abortControllerRef?.current) {
        abortControllerRef?.current?.abort();
      }
    };
  }, [authLoading, user, searchParams, location?.state]);

  const loadCatalogById = async (catalogId) => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);
      const catalog = await catalogService?.getCatalogById(catalogId);

      if (controller?.signal?.aborted) return;
      
      if (!catalog) {
        throw new Error('Catalog not found');
      }
      
      setCatalogData(catalog);
      
      // Load pages with dimensions - store ALL pages for metadata
      const catalogPages = await catalogService?.getCatalogPages(catalogId);
      if (controller?.signal?.aborted) return;
      setPages(catalogPages || []);
      
      // Load hotspots
      const catalogHotspots = await hotspotService?.getHotspotsByCatalog(catalogId);
      if (controller?.signal?.aborted) return;
      setHotspots(catalogHotspots || []);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Error loading catalog:', error);
      setError(error?.message || 'Failed to load catalog');
    } finally {
      if (!controller?.signal?.aborted) {
        setLoading(false);
      }
    }
  };

  const loadCatalogData = async () => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);
      const catalogs = await catalogService?.getCatalogs();
      if (controller?.signal?.aborted) return;
      if (catalogs && catalogs?.length > 0) {
        const catalog = catalogs?.[0];
        setCatalogData(catalog);
        setPages(catalog?.catalogPages || []);
        setHotspots(catalog?.hotspots || []);
      } else {
        setError('No catalogs found. Please create a catalog first.');
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Error loading catalog:', error);
      setError(error?.message || 'Failed to load catalog data');
    } finally {
      if (!controller?.signal?.aborted) {
        setLoading(false);
      }
    }
  };

  const loadPDFPages = async (pdf) => {
    try {
      setLoading(true);
      setProcessingPDF(true);
      setError(null);
      setAutoSaveStatus('processing');

      // Create a new catalog for this PDF
      const newCatalog = await catalogService?.createCatalog({
        title: pdf?.title,
        description: `Catalog created from ${pdf?.title}`,
        totalPages: pdf?.pageCount,
        status: 'draft',
        thumbnail: pdf?.thumbnail,
        thumbnailAlt: `${pdf?.title} cover`
      });

      setCatalogData(newCatalog);

      // Convert PDF pages to images
      const pdfPages = await convertPDFToImages(pdf);
      
      // Create catalog_pages records in database
      const pagePromises = pdfPages?.map((pageData, index) => {
        const pageNumber = index + 1;
        return {
          pageNumber,
          imageUrl: pageData?.imageUrl || pdf?.thumbnail,
          imageAlt: `${pdf?.title} - Page ${pageNumber}`,
          thumbnailUrl: pageData?.thumbnailUrl || pdf?.thumbnail,
          thumbnailAlt: `Thumbnail of page ${pageNumber}`,
          width: pageData?.width || 800,
          height: pageData?.height || 1200
        };
      });

      const catalogPagesData = await Promise.all(pagePromises);
      
      // Save pages to database
      const savedPages = await catalogService?.createCatalogPages(newCatalog?.id, catalogPagesData);
      setPages(savedPages);
      setHotspots([]);
      setAutoSaveStatus('saved');
      setProcessingPDF(false);

      // Clear the location state to prevent reprocessing on refresh
      navigate(location?.pathname, { replace: true, state: {} });
    } catch (error) {
      console.error('Error loading PDF pages:', error);
      setError(error?.message || 'Failed to process PDF');
      setAutoSaveStatus('error');
      setProcessingPDF(false);
    } finally {
      setLoading(false);
    }
  };

  const convertPDFToImages = async (pdf) => {
    // This is a simplified version - in production you'd use the actual PDF file
    // For now, we'll create placeholder page data based on the PDF metadata
    const pages = [];
    for (let i = 0; i < pdf?.pageCount; i++) {
      pages?.push({
        pageNumber: i + 1,
        imageUrl: pdf?.thumbnail, // Placeholder - would be actual page image
        thumbnailUrl: pdf?.thumbnail
      });
    }
    return pages;
  };

  const handleAddHotspot = async ({ x, y, width, height, page }) => {
    if (!catalogData) {
      console.error('[HOTSPOT CREATE] No catalog data available');
      setToasts(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        message: 'Cannot create hotspot: No catalog loaded'
      }]);
      return;
    }

    console.log('[HOTSPOT CREATE] Starting creation:', { x, y, width, height, page, type: selectedHotspotType });

    const newHotspotData = {
      catalogId: catalogData?.id,
      pageNumber: page,
      x,
      y,
      width: width || 200,
      height: height || 60,
      type: selectedHotspotType,
      visible: true,
      buttonColor: '#D69E2E',
      buttonText: selectedHotspotType === 'buy' ? 'Buy Now' : selectedHotspotType === 'link' ? 'Learn More' : 'Info'
    };

    console.log('[HOTSPOT CREATE] Data prepared:', newHotspotData);

    try {
      setCreatingHotspot(true);
      setAutoSaveStatus('saving');
      const newHotspot = await hotspotService?.createHotspot(newHotspotData);
      console.log('[HOTSPOT CREATE] API response:', newHotspot);
      setHotspots([...hotspots, newHotspot]);
      setSelectedHotspotId(newHotspot?.id);
      setShowHotspotPanel(true);
      setAutoSaveStatus('saved');
      console.log('[HOTSPOT CREATE] Success - hotspot added to state');
      
      setToasts(prev => [...prev, {
        id: Date.now(),
        type: 'success',
        message: t('catalogEditor.success.hotspotCreated')
      }]);
    } catch (error) {
      console.error('[HOTSPOT CREATE] Error:', error);
      setAutoSaveStatus('error');
      setToasts(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        message: t('catalogEditor.errors.hotspotCreateFailed', { message: error?.message || t('catalogEditor.errors.unknownError') })
      }]);
    } finally {
      setCreatingHotspot(false);
    }
  };

  const handleUpdateHotspot = async (id, config) => {
    try {
      setAutoSaveStatus('saving');
      const updated = await hotspotService?.updateHotspot(id, config);
      setHotspots(hotspots?.map(h => h?.id === id ? updated : h));
      setAutoSaveStatus('saved');
    } catch (error) {
      console.error('Error updating hotspot:', error);
      setAutoSaveStatus('error');
    }
  };

  const handleDeleteHotspot = async (id) => {
    try {
      setAutoSaveStatus('saving');
      await hotspotService?.deleteHotspot(id);
      setHotspots(hotspots?.filter(h => h?.id !== id));
      if (selectedHotspotId === id) {
        setSelectedHotspotId(null);
        setShowHotspotPanel(false);
      }
      setAutoSaveStatus('saved');
    } catch (error) {
      console.error('Error deleting hotspot:', error);
      setAutoSaveStatus('error');
    }
  };

  const handleToggleVisibility = (id) => {
    setHotspots(hotspots?.map(h => h?.id === id ? { ...h, visible: !h?.visible } : h));
  };

  const handleKeywordsUpdated = (pageId, keywords) => {
    setPages((prev) => prev.map((p) => p.id === pageId ? { ...p, keywords } : p));
  };

  const handlePublish = () => {
    if (publishTimeoutRef.current) clearTimeout(publishTimeoutRef.current);
    setAutoSaveStatus('publishing');
    publishTimeoutRef.current = setTimeout(() => {
      publishTimeoutRef.current = null;
      setAutoSaveStatus('saved');
      navigate('/embed-settings');
    }, 1500);
  };

  const handlePublishCatalog = async () => {
    if (!catalogData) return;
    
    try {
      setIsPublishing(true);
      setAutoSaveStatus('publishing');
      const updated = await catalogService?.publishCatalog(catalogData?.id);
      setCatalogData(updated);
      setAutoSaveStatus('saved');
    } catch (error) {
      console.error('Error publishing catalog:', error);
      setAutoSaveStatus('error');
      alert('Failed to publish catalog. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleUnpublishCatalog = () => {
    setUnpublishError(null);
    setShowUnpublishModal(true);
  };

  const handleUnpublishConfirm = async () => {
    if (!catalogData) return;
    try {
      setIsPublishing(true);
      setAutoSaveStatus('saving');
      const updated = await catalogService?.unpublishCatalog(catalogData?.id);
      setCatalogData(updated);
      setAutoSaveStatus('saved');
      setShowUnpublishModal(false);
    } catch (error) {
      console.error('Error unpublishing catalog:', error);
      setAutoSaveStatus('error');
      setUnpublishError(t('catalogEditor.unpublishModal.error'));
    } finally {
      setIsPublishing(false);
    }
  };

  const handleArchiveConfirm = async () => {
    if (!catalogData) return;
    try {
      setIsArchiving(true);
      await catalogService?.archiveCatalog(catalogData?.id);
      setShowArchiveModal(false);
      navigate('/dashboard');
    } catch (error) {
      console.error('Error archiving catalog:', error);
      setToasts(prev => [...prev, { id: Date.now(), type: 'error', message: t('catalogEditor.modals.archive.title') }]);
      setShowArchiveModal(false);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleStartEditTitle = () => {
    setEditedTitle(catalogData?.title || '');
    setIsEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!catalogData || !editedTitle?.trim()) {
      setIsEditingTitle(false);
      return;
    }
    
    try {
      setAutoSaveStatus('saving');
      const updated = await catalogService?.renameCatalog(catalogData?.id, editedTitle?.trim());
      setCatalogData(updated);
      setIsEditingTitle(false);
      setAutoSaveStatus('saved');
    } catch (error) {
      console.error('Error renaming catalog:', error);
      setAutoSaveStatus('error');
      alert('Failed to rename catalog. Please try again.');
    }
  };

  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditedTitle('');
  };

  const handleOpenCopyModal = (hotspotId) => {
    const hotspot = hotspots?.find(h => h?.id === hotspotId?.id);
    if (hotspot) {
      setIsCopyModalOpen(true);
      setHotspotToCopy(hotspot);
    }
  };

  const handleCopyHotspot = async (targetPages) => {
    if (!hotspotToCopy) return;

    try {
      setAutoSaveStatus('saving');
      
      // Copy hotspot to each selected page
      const copyPromises = targetPages?.map(pageNumber => {
        return hotspotService?.createHotspot({ 
          ...hotspotToCopy, 
          id: undefined, // Remove ID so a new one is generated
          catalogId: catalogData?.id,
          pageNumber: pageNumber 
        });
      });
      
      const newHotspots = await Promise.all(copyPromises);
      setHotspots(prev => [...prev, ...newHotspots]);
      setIsCopyModalOpen(false);
      setHotspotToCopy(null);
      setAutoSaveStatus('saved');
      
      // Show success toast
      setToasts(prev => [...prev, {
        id: Date.now(),
        type: 'success',
        message: `Hotspot copied to ${targetPages?.length} page(s) successfully`
      }]);
    } catch (error) {
      console.error('Error copying hotspot:', error);
      setAutoSaveStatus('error');
      
      // Show error toast
      setToasts(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        message: `Failed to copy hotspot: ${error?.message || 'Unknown error'}`
      }]);
    }
  };

  const selectedHotspot = hotspots?.find(h => h?.id === selectedHotspotId);
  
  // Memoize hotspot counts to avoid recalculation on every render
  const hotspotCounts = useMemo(() => {
    return hotspots?.reduce((acc, h) => {
      acc[h?.pageNumber] = (acc?.[h?.pageNumber] || 0) + 1;
      return acc;
    }, {});
  }, [hotspots]);

  // NEW: Filter hotspots based on currently visible page(s)
  const visibleHotspots = useMemo(() => {
    if (!catalogData?.totalPages) return hotspots;

    const totalPages = catalogData?.totalPages;
    const isFrontCover = currentPage === 1;
    const isBackCover = currentPage === totalPages;

    if (isFrontCover) {
      // Front cover: show only page 1 hotspots
      return hotspots?.filter(h => h?.pageNumber === 1);
    } else if (isBackCover) {
      // Back cover: show only last page hotspots
      return hotspots?.filter(h => h?.pageNumber === totalPages);
    } else {
      // Spread: show hotspots from both pages
      // Pages are paired: 2-3, 4-5, 6-7, etc.
      const leftPageNum = currentPage % 2 === 0 ? currentPage : currentPage - 1;
      const rightPageNum = leftPageNum + 1;
      return hotspots?.filter(h => h?.pageNumber === leftPageNum || h?.pageNumber === rightPageNum);
    }
  }, [hotspots, currentPage, catalogData?.totalPages]);

  if (authLoading || loading) {
    return processingPDF ? (
      <div className="flex h-screen items-center justify-center bg-background">
        <LoadingSpinner size="lg" text="Processing PDF pages..." />
      </div>
    ) : (
      <EditorSkeleton />
    );
  }

  if (error || !catalogData) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
        <div className="flex-1 flex items-center justify-center lg:ml-[280px]">
          <ErrorState
            title={error ? 'Failed to Load Editor' : 'No Catalog Found'}
            message={error || 'Please select a catalog to edit or create a new one.'}
            onRetry={error ? loadCatalogData : null}
            showRetry={!!error}
            retryText="Retry"
          />
          {!error && (
            <div className="mt-4 flex gap-3">
              <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
              <Button variant="outline" onClick={() => navigate('/pdf-upload')}>Upload PDF</Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggle={setIsSidebarCollapsed} />
      <div className={`flex-1 flex flex-col transition-all duration-250 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-[280px]'}`}>
        {/* Top Bar */}
        <header className="h-16 md:h-20 bg-card border-b border-border flex items-center justify-between px-4 md:px-6 lg:px-8">
          <div className="flex items-center gap-3 md:gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-md hover:bg-muted transition-base"
              aria-label={t('catalogEditor.backToDashboard')}
            >
              <Icon name="ArrowLeft" size={20} />
            </button>
            <div className="flex-1">
              {isEditingTitle ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e?.target?.value)}
                    onKeyDown={(e) => {
                      if (e?.key === 'Enter') handleSaveTitle();
                      if (e?.key === 'Escape') handleCancelEditTitle();
                    }}
                    className="text-base md:text-lg font-semibold"
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Check"
                    onClick={handleSaveTitle}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="X"
                    onClick={handleCancelEditTitle}
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h1 className="text-base md:text-lg font-semibold text-foreground">
                    {catalogData?.title}
                  </h1>
                  <button
                    onClick={handleStartEditTitle}
                    className="p-1 hover:bg-muted rounded transition-base"
                    aria-label={t('catalogEditor.title')}
                  >
                    <Icon name="Edit2" size={16} />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  catalogData?.status === 'published' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                }`}>
                  {catalogData?.status === 'published' ? t('catalogEditor.status.published') : t('catalogEditor.status.draft')}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  autoSaveStatus === 'saved' ? 'bg-success/10 text-success' :
                  autoSaveStatus === 'saving' ? 'bg-warning/10 text-warning' :
                  autoSaveStatus === 'processing' ? 'bg-accent/10 text-accent' :
                  autoSaveStatus === 'publishing' ? 'bg-accent/10 text-accent' : 'bg-destructive/10 text-destructive'
                }`}>
                  {autoSaveStatus === 'saved' ? t('catalogEditor.autoSave.saved') :
                   autoSaveStatus === 'saving' ? t('catalogEditor.autoSave.saving') :
                   autoSaveStatus === 'processing' ? t('catalogEditor.autoSave.processing') :
                   autoSaveStatus === 'publishing' ? t('catalogEditor.autoSave.publishing') : t('catalogEditor.autoSave.error')}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            {catalogData?.status === 'draft' && (
              <Button
                variant="ghost"
                size="sm"
                iconName="Package"
                iconPosition="left"
                onClick={() => setShowArchiveModal(true)}
                loading={isArchiving}
              >
                {t('catalogEditor.actions.archive')}
              </Button>
            )}
            {catalogData?.status === 'published' ? (
              <Button
                variant="outline"
                size="sm"
                iconName="EyeOff"
                iconPosition="left"
                onClick={handleUnpublishCatalog}
                loading={isPublishing}
              >
                {t('catalogEditor.unpublish')}
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                iconName="Upload"
                iconPosition="left"
                onClick={handlePublishCatalog}
                loading={isPublishing}
              >
                {t('catalogEditor.publish')}
              </Button>
            )}
          </div>
        </header>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row relative">
          {/* Left Panel - Hotspot Types */}
          <div className={`relative border-b lg:border-b-0 lg:border-r border-border flex-shrink-0 transition-all duration-250 ${leftPanelCollapsed ? 'lg:w-0 overflow-hidden' : 'lg:w-80'}`}>
            <HotspotToolbar
              onTypeSelect={setSelectedHotspotType}
              selectedType={selectedHotspotType}
            />

            <div className="hidden lg:block">
              <HotspotList
                hotspots={visibleHotspots}
                onSelect={(id) => {
                  setSelectedHotspotId(id);
                  setShowHotspotPanel(true);
                }}
                onDelete={handleDeleteHotspot}
                onToggleVisibility={handleToggleVisibility}
                onCopy={handleOpenCopyModal}
                selectedId={selectedHotspotId}
                onNavigateToPage={(pageNumber) => {
                  // Navigate to the page where the hotspot is configured
                  setCurrentPage(pageNumber);
                }}
              />
            </div>
          </div>

          {/* Center - Canvas */}
          <div className="flex-1 flex flex-col relative">
            {/* Left panel toggle button */}
            <button
              onClick={() => setLeftPanelCollapsed(v => !v)}
              className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 w-5 h-10 items-center justify-center bg-card border border-border rounded-full shadow-sm hover:bg-muted transition-colors"
              title={leftPanelCollapsed ? t('catalogEditor.panels.showLeft') : t('catalogEditor.panels.hideLeft')}
            >
              <Icon name={leftPanelCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={12} />
            </button>

            {/* Right panel toggle button */}
            <button
              onClick={() => setRightPanelCollapsed(v => !v)}
              className="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-20 w-5 h-10 items-center justify-center bg-card border border-border rounded-full shadow-sm hover:bg-muted transition-colors"
              title={rightPanelCollapsed ? t('catalogEditor.panels.showRight') : t('catalogEditor.panels.hideRight')}
            >
              <Icon name={rightPanelCollapsed ? 'ChevronLeft' : 'ChevronRight'} size={12} />
            </button>

            <PageCanvas
              currentPage={currentPage}
              totalPages={catalogData?.totalPages}
              pages={pages}
              onPageChange={setCurrentPage}
              hotspots={hotspots}
              onHotspotAdd={handleAddHotspot}
              onHotspotUpdate={handleUpdateHotspot}
              onHotspotSelect={(id) => {
                setSelectedHotspotId(id);
                setShowHotspotPanel(true);
              }}
              selectedHotspot={selectedHotspotId}
              zoom={zoom}
              onZoomChange={setZoom}
              creatingHotspot={creatingHotspot}
            />

          </div>

          {/* Right Panel - Hotspot Config / Menu Mapping / Page Keywords (Desktop) */}
          <div className={`hidden lg:block border-l border-border flex-shrink-0 transition-all duration-250 ${rightPanelCollapsed ? 'lg:w-0 overflow-hidden' : 'lg:w-96'}`}>
            {/* Tab toggle */}
            <div className="flex border-b border-border">
              {[
                { id: 'hotspots', icon: 'MousePointer', label: t('catalogEditor.tabs.hotspots') },
                { id: 'menu',     icon: 'Menu',         label: t('catalogEditor.tabs.menu') },
                { id: 'page',     icon: 'Search',       label: t('catalogEditor.tabs.search') },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => { setRightTab(tab.id); setShowMenuPanel(tab.id === 'menu'); }}
                  className={`flex-1 px-2 py-2 text-xs font-medium flex items-center justify-center gap-1 transition-colors ${rightTab === tab.id ? 'text-accent border-b-2 border-accent' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  <Icon name={tab.icon} size={12} /> {tab.label}
                </button>
              ))}
            </div>
            {rightTab === 'menu' ? (
              <MenuMappingPanel
                catalogId={catalogData?.id}
                totalPages={pages?.length}
              />
            ) : rightTab === 'page' ? (
              <PageKeywordsPanel
                page={pages?.find((p) => p.pageNumber === currentPage)}
                pages={pages || []}
                totalPages={pages?.length}
                onKeywordsUpdated={handleKeywordsUpdated}
              />
            ) : (
              <HotspotConfigPanel
                hotspot={selectedHotspot ? { ...selectedHotspot, catalogPages: pages } : null}
                onUpdate={handleUpdateHotspot}
                onDelete={handleDeleteHotspot}
                onCopy={handleOpenCopyModal}
                onClose={() => {
                  setSelectedHotspotId(null);
                  setShowHotspotPanel(false);
                }}
              />
            )}
          </div>
        </div>

        {/* Mobile Hotspot Config Panel */}
        {showHotspotPanel && (
          <div className="lg:hidden fixed inset-0 z-40 bg-background">
            <HotspotConfigPanel
              hotspot={selectedHotspot ? { ...selectedHotspot, catalogPages: pages } : null}
              onUpdate={handleUpdateHotspot}
              onDelete={handleDeleteHotspot}
              onCopy={handleOpenCopyModal}
              onClose={() => {
                setSelectedHotspotId(null);
                setShowHotspotPanel(false);
              }}
            />
          </div>
        )}

        {/* Mobile Hotspot List */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border max-h-64 overflow-y-auto z-30">
          <HotspotList
            hotspots={visibleHotspots}
            onSelect={(id) => {
              setSelectedHotspotId(id);
              setShowHotspotPanel(true);
            }}
            onDelete={handleDeleteHotspot}
            onToggleVisibility={handleToggleVisibility}
            onCopy={handleOpenCopyModal}
            selectedId={selectedHotspotId}
            onNavigateToPage={(pageNumber) => {
              // Navigate to the page where the hotspot is configured
              setCurrentPage(pageNumber);
            }}
          />
        </div>
      </div>
      {/* Preview Modal */}
      <PreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        pages={pages}
        hotspots={hotspots}
        catalogTitle={catalogData?.title}
      />

      {/* Copy Hotspot Modal */}
      <CopyHotspotModal
        isOpen={isCopyModalOpen}
        onClose={() => {
          setIsCopyModalOpen(false);
          setHotspotToCopy(null);
        }}
        hotspot={hotspotToCopy}
        pages={pages}
        currentPage={currentPage}
        onConfirm={handleCopyHotspot}
      />

      {/* Unpublish Confirmation Modal */}
      <ConfirmationModal
        isOpen={showUnpublishModal}
        onClose={() => { setShowUnpublishModal(false); setUnpublishError(null); }}
        onConfirm={handleUnpublishConfirm}
        title={t('catalogEditor.unpublishModal.title')}
        message={t('catalogEditor.unpublishModal.message', { title: catalogData?.title })}
        confirmText={t('catalogEditor.unpublishModal.confirm')}
        cancelText={t('catalogEditor.unpublishModal.cancel')}
        variant="warning"
        icon="EyeOff"
        isLoading={isPublishing}
        error={unpublishError}
      />

      {/* Archive Confirmation Modal */}
      <ConfirmationModal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        onConfirm={handleArchiveConfirm}
        title={t('catalogEditor.modals.archive.title')}
        message={t('catalogEditor.modals.archive.description')}
        confirmText={t('catalogEditor.modals.archive.confirmButton')}
        cancelText={t('catalogEditor.modals.archive.cancelButton')}
        variant="warning"
        icon="Package"
        isLoading={isArchiving}
      />

      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {toasts?.map((toast) => (
          <Toast
            key={toast?.id}
            message={toast?.message}
            type={toast?.type}
            duration={toast?.duration}
            onClose={() => removeToast(toast?.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default CatalogEditor;